import InvoicingSettings from './InvoicingSettings';
import MetaAdsCard from './MetaAdsCard';
import { DrivesRegister } from '../../components/DrivesRegister';
// Settings restructured into tabs per mockups-operator-upgrades-2026-07.html
// §8 (approved) — General (appearance, calendar feed, task templates,
// about) · Security (password change, TOTP, sessions, sign-out-everywhere)
// · Data (backups, trash, unlinked artifacts, dashboards integration —
// secret status only, never the value — error log) · Automation (the
// email-gated background jobs: reminder ladder, testimonial ask, stale-
// proposal nudge, shoot-day nudge). "Sign out everywhere" lives in Security;
// the temporary sidebar Log out item is gone (phase 9).

import { useViewState } from '../../lib/useViewState';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { applyTheme, currentTheme, type Theme } from '../../lib/theme';
import { setMoneyMode, useMoneyMode } from '../../lib/money';
import {
  mergeSidebarLayout,
  setSidebarLayout,
  useSidebarLayout,
  type SidebarLayout,
} from '../../lib/sidebarLayout';
import {
  DEFAULT_SIDEBAR_LAYOUT,
  SIDEBAR_GROUP_LABELS,
  SIDEBAR_NAV_ITEMS,
} from '../../lib/sidebarNav';
import { formatProjectNumber, Dropdown, Seg, SkeletonRows, Tabs } from '../../components/primitives';
import { PageHeader } from '../../components/primitives';
import { Sheet, SheetButton } from '../../components/Sheet';
import { PickerSheet } from '../../components/PickerSheet';
import { FollowupTasksCard } from '../../components/FollowupTasksCard';
import { useIsMobile } from '../../lib/useIsMobile';
import { truncateCalendarUrl, taskTemplateCountLabel } from '../../lib/settingsMobile';
import { todayLabel } from '../../components/primitives';
import { SIDEBAR_GROUPS, type SidebarGroup } from '../../../shared/prefs';
import { ARTIFACT_TOOL_LABELS, type UnlinkedArtifactRow } from '../../../shared/types';

import type { BackupItem, DashboardsHealth, ErrorItem, ProjectOption, SettingsTab, TaskTemplate, TrashItem } from './types';
import { TABLE_LABELS, TRASH_WINDOW, copySidebarLayout, deletedLabel, moveSidebarItem, timeLabel } from './helpers';
import { settingsViewCodec } from './viewState';
import { TemplateModal } from './TemplateModal';
import { ChangePasswordModal } from './ChangePasswordModal';
import { TotpModal } from './TotpModal';

/** "Work, Growth, Money and Tools" — follows SIDEBAR_GROUPS, so a new group needs no copy edit. */
const groupList = SIDEBAR_GROUPS.map((g) => SIDEBAR_GROUP_LABELS[g])
  .reduce((acc, label, i, all) => (i === 0 ? label : `${acc}${i === all.length - 1 ? ' and ' : ', '}${label}`), '');

export default function Settings({ onSignedOut }: { onSignedOut?: () => void }) {
  const { initial, publish } = useViewState(settingsViewCodec, state => {
    setTab(state.tab);
    setShowAllTrash(state.showAllTrash);
  });
  const isMobile = useIsMobile();
  const [deleteProject, setDeleteProject] = useState<TrashItem | null>(null);
  const [tab, setTab] = useState<SettingsTab>(() => initial.tab);
  const [theme, setTheme] = useState<Theme>(currentTheme());
  const moneyMode = useMoneyMode();
  const savedSidebarLayout = useSidebarLayout();
  const [sidebarDraft, setSidebarDraft] = useState<SidebarLayout>(() =>
    mergeSidebarLayout(savedSidebarLayout, DEFAULT_SIDEBAR_LAYOUT),
  );
  const [sidebarDirty, setSidebarDirty] = useState(false);
  const [totp, setTotp] = useState<{ enabled: boolean; pending: boolean } | null>(null);
  const [sessions, setSessions] = useState<number | null>(null);
  const [trash, setTrash] = useState<TrashItem[] | null>(null);
  const [backups, setBackups] = useState<BackupItem[] | null>(null);
  const [integration, setIntegration] = useState<DashboardsHealth | null>(null);
  const [emailReady, setEmailReady] = useState<{ configured: boolean; paused: boolean } | null>(null);
  const [calendarToken, setCalendarToken] = useState<string | null>(null);
  const [errors, setErrors] = useState<ErrorItem[] | null>(null);
  const [rows, setRows] = useState<number | null>(null);
  const [unlinkedArtifacts, setUnlinkedArtifacts] = useState<UnlinkedArtifactRow[] | null>(null);
  const [projectOptions, setProjectOptions] = useState<ProjectOption[]>([]);
  const [assigning, setAssigning] = useState<string | null>(null);
  const [showAllTrash, setShowAllTrash] = useState(() => initial.showAllTrash);

  useEffect(() => {
    publish({ tab, showAllTrash });
  }, [publish, tab, showAllTrash]);
  const [emptyArming, setEmptyArming] = useState(false);
  const [templates, setTemplates] = useState<TaskTemplate[]>([]);
  const [templateModal, setTemplateModal] = useState<'new' | TaskTemplate | null>(null);
  const [modal, setModal] = useState<'password' | 'totp' | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Follow-up task autosaves flash the header live-label ("Saved") briefly;
  // on mobile that label is the PageHeader's, so no toast as well.
  const flashRef = useRef<string | null>(null);
  const flashSaved = useCallback((message: string) => {
    flashRef.current = message;
    setMsg(message);
    window.setTimeout(() => setMsg((m) => (m === message ? null : m)), 1800);
  }, []);
  // The card clears only the error it raised — never another action's.
  const followupErrorRef = useRef<string | null>(null);
  const setFollowupError = useCallback((message: string | null) => {
    if (message) {
      followupErrorRef.current = message;
      setError(message);
      return;
    }
    const own = followupErrorRef.current;
    followupErrorRef.current = null;
    if (own) setError((e) => (e === own ? null : e));
  }, []);

  // `run()` reloads after every action, so two actions in quick succession can
  // have their loads land out of order — only the newest one may write state.
  // (No `ss:mutated` listener here: Phase 0's hidesFab() keeps the quick-add
  // FAB off /settings, so nothing mutates this page's data from elsewhere.)
  const loadSequence = useRef(0);
  const load = useCallback(async () => {
    const sequence = ++loadSequence.current;
    const [t, tr, b, s, integ, about, tmpl, cal, errs, unlinked, projs] = await Promise.all([
      api<{ enabled: boolean; pending: boolean }>('/api/auth/totp'),
      api<{ items: TrashItem[] }>('/api/admin/system/trash'),
      api<{ backups: BackupItem[] }>('/api/admin/system/backups').catch(() => ({ backups: [] })),
      api<{ count: number }>('/api/auth/sessions').catch(() => ({ count: 0 })),
      api<{ dashboards: DashboardsHealth; email: { configured: boolean; paused: boolean } }>(
        '/api/admin/system/integration',
      ).catch(() => ({
        dashboards: { configured: false, healthy: false, status: null },
        email: { configured: false, paused: false },
      })),
      api<{ rows: number }>('/api/admin/system/about').catch(() => ({ rows: 0 })),
      api<{ templates: TaskTemplate[] }>('/api/admin/task-templates').catch(() => ({ templates: [] })),
      api<{ token: string }>('/api/admin/system/calendar-token').catch(() => ({ token: null as never })),
      api<{ errors: ErrorItem[] }>('/api/admin/system/errors').catch(() => ({ errors: [] })),
      api<{ items: UnlinkedArtifactRow[] }>('/api/admin/system/unlinked-artifacts').catch(() => ({ items: [] })),
      api<{ projects: ProjectOption[] }>('/api/admin/projects').catch(() => ({ projects: [] })),
    ]);
    if (sequence !== loadSequence.current) return;
    setTotp(t);
    setTrash(tr.items);
    setBackups(b.backups);
    setSessions(s.count);
    setIntegration(integ.dashboards);
    setEmailReady(integ.email);
    setRows(about.rows);
    setTemplates(tmpl.templates);
    setCalendarToken(cal.token);
    setErrors(errs.errors);
    setUnlinkedArtifacts(unlinked.items);
    // Nothing new gets assigned to a voided project.
    setProjectOptions(projs.projects.filter((p) => p.voided_at === null));
  }, []);

  useEffect(() => {
    load().catch((e) => setError(e.message));
    return () => {
      loadSequence.current++;
    };
  }, [load]);

  // Mobile has no header-right slot, so a success message is a toast — it has
  // to time out on its own the way the desktop strip is simply overwritten.
  useEffect(() => {
    if (!isMobile || !msg) return;
    const timer = setTimeout(() => setMsg(null), 3500);
    return () => clearTimeout(timer);
  }, [isMobile, msg]);

  useEffect(() => {
    if (!emptyArming) return;
    const t = setTimeout(() => setEmptyArming(false), 4000);
    return () => clearTimeout(t);
  }, [emptyArming]);

  useEffect(() => {
    if (!sidebarDirty) {
      setSidebarDraft(mergeSidebarLayout(savedSidebarLayout, DEFAULT_SIDEBAR_LAYOUT));
    }
  }, [savedSidebarLayout, sidebarDirty]);

  const run = async (fn: () => Promise<void>, success?: string) => {
    setBusy(true);
    setError(null);
    setMsg(null);
    try {
      await fn();
      if (success) setMsg(success);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed.');
    } finally {
      setBusy(false);
    }
  };

  const dismissError = async (id: number) => {
    await api(`/api/admin/system/errors/${id}/dismiss`, { method: 'POST' });
    setErrors((current) => current?.filter((e) => e.id !== id) ?? []);
  };

  const dismissAllErrors = async () => {
    await api('/api/admin/system/errors/dismiss-all', { method: 'POST' });
    setErrors([]);
  };

  const calendarUrl = calendarToken
    ? `${window.location.origin}/calendar.ics?token=${calendarToken}`
    : null;

  const switchTheme = (t: Theme) => {
    applyTheme(t);
    setTheme(t);
  };

  const updateSidebarItem = (group: SidebarGroup, index: number, direction: 'up' | 'down') => {
    setSidebarDraft((current) => moveSidebarItem(current, group, index, direction));
    setSidebarDirty(true);
  };

  // The order applies locally either way, but it isn't "saved" until D1 has
  // it — a failed write keeps the card dirty so the save can be retried.
  const saveSidebar = async () => {
    try {
      await setSidebarLayout(sidebarDraft);
      setSidebarDirty(false);
      setError(null);
      setMsg('Sidebar layout saved — reload to apply');
    } catch (e) {
      setMsg(null);
      setError(e instanceof Error ? e.message : 'Could not save the sidebar layout.');
    }
  };

  const resetSidebar = async () => {
    const defaults = copySidebarLayout(DEFAULT_SIDEBAR_LAYOUT);
    setSidebarDraft(defaults);
    try {
      await setSidebarLayout(defaults);
      setSidebarDirty(false);
      setError(null);
      setMsg('Sidebar layout reset — reload to apply');
    } catch (e) {
      setSidebarDirty(true);
      setMsg(null);
      setError(e instanceof Error ? e.message : 'Could not save the sidebar layout.');
    }
  };

  // Stay in sync when the sidebar Theme toggle changes the preference while
  // this page is open (applyTheme emits ss:theme).
  useEffect(() => {
    const onTheme = () => setTheme(currentTheme());
    window.addEventListener('ss:theme', onTheme);
    return () => window.removeEventListener('ss:theme', onTheme);
  }, []);

  const signOutEverywhere = async () => {
    await api('/api/auth/logout-all', { method: 'POST' });
    onSignedOut?.();
  };

  const visibleTrash = showAllTrash ? trash : trash?.slice(0, TRASH_WINDOW);

  // Restoring a project brings its tasks and invoices back with it; both
  // breakpoints report exactly what came along.
  const restoreTrashItem = (t: TrashItem) =>
    run(async () => {
      const r = await api<{
        restored_tasks?: number;
        restored_invoices?: number;
        restored_project?: boolean;
      }>('/api/admin/system/trash/restore', {
        method: 'POST',
        json: { table: t.table, id: t.id },
      });
      const bits: string[] = [];
      if (r.restored_tasks) bits.push(`${r.restored_tasks} task${r.restored_tasks === 1 ? '' : 's'}`);
      if (r.restored_invoices)
        bits.push(`${r.restored_invoices} invoice${r.restored_invoices === 1 ? '' : 's'}`);
      if (r.restored_project) bits.push('its project');
      setMsg(bits.length ? `Restored — with ${bits.join(' + ')}` : 'Restored');
    });

  // Wave A3 — proposal registrations that didn't confidently match a client
  // by name; manual "assign to project" moves them into project_artifacts.
  const assignArtifact = (artifactId: string, projectId: string) =>
    run(async () => {
      await api(`/api/admin/system/unlinked-artifacts/${artifactId}/assign`, {
        method: 'POST',
        json: { project_id: projectId },
      });
    }, 'Assigned to project');

  return (
    <>
      {isMobile ? <PageHeader title="Settings" liveLabel={msg ?? 'Systems OK'} /> : <header className="page-header">
        <span className="page-title">Settings</span>
        <div className="header-right">
          {msg && (
            <span className="live">
              <span className="live-dot" />
              {msg}
            </span>
          )}
          {error && <span className="error-text">{error}</span>}
          {!msg && !error && (
            <span className="live">
              <span className="live-dot" />
              Systems OK
            </span>
          )}
          <span>{todayLabel()}</span>
        </div>
      </header>}
      {isMobile && msg && msg !== flashRef.current && <div className="settings-toast" role="status">{msg}</div>}
      {isMobile && error && <div className="settings-mobile-error" role="alert">{error}</div>}

      <div className={isMobile ? "settings-mobile-tabs" : "toolbar"}>
        <Tabs
          options={[
            { value: 'general', label: 'General' },
            { value: 'security', label: 'Security' },
            { value: 'data', label: 'Data' },
            { value: 'automation', label: 'Automation' },
            { value: 'registers', label: 'Registers' },
            { value: 'invoicing', label: 'Invoicing' },
          ]}
          value={tab}
          onChange={setTab}
        />
      </div>

      <div className="page-body">
        <div className={isMobile ? "content wide settings-mobile" : "content wide"}>
          {tab === 'registers' && <DrivesRegister />}
          {tab === 'invoicing' && <InvoicingSettings />}
          {tab === 'general' && (
            <div className="settings-grid">
              {/* Meta Ads (Marketing Phase B) — full width, first, per the mockup. */}
              <MetaAdsCard mobile={isMobile} />
              {/* Appearance */}
              <div className="settings-card">
                <div className="sc-head">
                  <span className="sc-title">Appearance</span>
                </div>
                <div className="sc-desc">
                  Dark is the operator default; light matches the public site palette.
                </div>
                <Seg
                  options={[
                    { value: 'dark', label: 'Dark' },
                    { value: 'light', label: 'Light' },
                    { value: 'system', label: 'System' },
                  ]}
                  value={theme}
                  onChange={switchTheme}
                />
                <div className="sc-desc" style={{ marginTop: 14 }}>
                  Money — quotes are ex GST, so amounts show ex GST by default.
                  BAS reports always stay incl. GST.
                </div>
                <Seg
                  options={[
                    { value: 'ex', label: 'Ex GST' },
                    { value: 'incl', label: 'Incl. GST' },
                  ]}
                  value={moneyMode}
                  onChange={setMoneyMode}
                />
              </div>

              {/* Sidebar layout — route ids are persisted cross-device; the
                  visible sidebar deliberately updates on the next reload. */}
              {isMobile ? <div className="settings-best"><b>Sidebar layout · best on desktop</b>Reorder {groupList} links at os.sheahanstudios.com on a laptop. The bottom bar and More sheet follow that order.</div> : <div className="settings-card sidebar-layout-card">
                <div className="sc-head">
                  <span className="sc-title">Sidebar layout</span>
                </div>
                <div className="sc-desc">
                  Reorder links within {groupList}. Moving past a group edge moves the
                  link into the adjacent group. Changes take effect after reload.
                </div>
                <div className="sidebar-layout-groups">
                  {SIDEBAR_GROUPS.map((group, groupIndex) => (
                    <div className="sidebar-layout-group" key={group}>
                      <div className="sidebar-layout-heading">{SIDEBAR_GROUP_LABELS[group]}</div>
                      {sidebarDraft[group].map((id, index) => {
                        const item = SIDEBAR_NAV_ITEMS[id];
                        const atFirstPosition = groupIndex === 0 && index === 0;
                        const atLastPosition =
                          groupIndex === SIDEBAR_GROUPS.length - 1 &&
                          index === sidebarDraft[group].length - 1;
                        return (
                          <div className="sidebar-layout-row" key={id}>
                            <item.icon className="sidebar-layout-icon" />
                            <span>{item.label}</span>
                            <div className="sidebar-layout-controls">
                              <button
                                type="button"
                                className="btn sidebar-layout-arrow"
                                aria-label={`Move ${item.label} up`}
                                disabled={atFirstPosition}
                                onClick={() => updateSidebarItem(group, index, 'up')}
                              >
                                ↑
                              </button>
                              <button
                                type="button"
                                className="btn sidebar-layout-arrow"
                                aria-label={`Move ${item.label} down`}
                                disabled={atLastPosition}
                                onClick={() => updateSidebarItem(group, index, 'down')}
                              >
                                ↓
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
                <div className="sidebar-layout-actions">
                  <button
                    type="button"
                    className="btn primary"
                    disabled={!sidebarDirty}
                    onClick={() => void saveSidebar()}
                  >
                    Save
                  </button>
                  <button type="button" className="btn" onClick={() => void resetSidebar()}>
                    Reset to defaults
                  </button>
                </div>
              </div>}

              {/* Calendar feed (phase 22) — read-only ICS of shoots/edits/
                  due dates + unpaid invoices; subscribe from Google Calendar
                  or an iPhone. Regenerating invalidates the old URL. */}
              <div className="settings-card">
                <div className="sc-head">
                  <span className="sc-title">Calendar feed</span>
                </div>
                <div className="sc-desc">
                  Read-only ICS of shoots, edits, due dates and unpaid invoices — subscribe from
                  Google Calendar or an iPhone.
                </div>
                <div className="sc-row">
                  <span>Feed URL</span>
                  <span className="meta mono feed-url" title={calendarUrl ?? '…'}>{calendarUrl ? (isMobile ? truncateCalendarUrl(calendarUrl) : calendarUrl) : '…'}</span>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <button
                    className={isMobile ? "cbtn acc" : "btn"}
                    disabled={!calendarUrl}
                    onClick={() => {
                      if (!calendarUrl) return;
                      navigator.clipboard.writeText(calendarUrl).then(() => setMsg(isMobile ? 'Copied' : 'Feed URL copied')).catch(() => setError('Could not copy the feed URL.'));
                    }}
                  >
                    Copy URL
                  </button>
                  <button
                    className={isMobile ? 'cbtn' : 'btn'}
                    disabled={busy}
                    onClick={() =>
                      run(async () => {
                        const { token } = await api<{ token: string }>(
                          '/api/admin/system/calendar-token/regenerate',
                          { method: 'POST' },
                        );
                        setCalendarToken(token);
                      }, 'Token regenerated — old feed URL is now invalid')
                    }
                  >
                    Regenerate token
                  </button>
                </div>
              </div>

              {/* Task templates */}
              <div className="settings-card">
                <div className="sc-head">
                  <span className="sc-title">Task templates</span>
                  <span className="sc-meta">{templates.length || 'none'}</span>
                </div>
                <div className="sc-desc">
                  Reusable checklists applied to a project in one go — tasks land due today,
                  re-date them as needed. Save one from any project’s task list.
                </div>
                {templates.map((t) => isMobile ? (
                  <button className="settings-link-row" key={t.id} onClick={() => setTemplateModal(t)}>
                    <span>{t.name}</span><span className="settings-row-action">{taskTemplateCountLabel(t.items.length)} ›</span>
                  </button>
                ) : (
                  <div className="sc-row" key={t.id}>
                    <span style={{ fontFamily: 'var(--font-body)', fontSize: 12.5 }}>{t.name}</span>
                    <span className="meta">
                      {t.items.length} task{t.items.length === 1 ? '' : 's'}
                    </span>
                    <span className="dl" onClick={() => setTemplateModal(t)}>
                      Edit
                    </span>
                  </div>
                ))}
                {isMobile && templates.length === 0 && <div className="empty-note">No templates yet — save a checklist to use again.</div>}
                <div style={{ marginTop: 12 }}>
                  <button
                    className={
                      isMobile
                        ? templates.length === 0
                          ? 'cbtn pri wide settings-wide'
                          : 'cbtn'
                        : 'btn'
                    }
                    onClick={() => setTemplateModal('new')}
                  >
                    + New template
                  </button>
                </div>
              </div>

              <div className="settings-card">
                <div className="sc-head">
                  <span className="sc-title">Tool sign-in</span>
                </div>
                <div className="sc-desc">
                  Scripts and Call sheets open already signed in — links from the OS carry a 60-second single-use sign-in token.
                </div>
              </div>

              {/* About */}
              <div className="settings-card">
                <div className="sc-head">
                  <span className="sc-title">About</span>
                </div>
                <div className="sc-row" style={{ borderTop: 'none', paddingTop: 0 }}>
                  <span>Version</span>
                  <span className="meta">{__BUILD_DATE__}</span>
                </div>
                <div className="sc-row">
                  <span>Database</span>
                  <span className="meta">D1 · hsp-os{rows !== null ? ` · ${rows.toLocaleString('en-AU')} rows` : ''}</span>
                </div>
                <div className="sc-row">
                  <span>Host</span>
                  <span className="meta">Cloudflare Workers · free tier</span>
                </div>
              </div>
            </div>
          )}

          {tab === 'security' && (
            <div className="settings-grid">
              {/* Security */}
              <div className="settings-card">
                <div className="sc-head">
                  <span className="sc-title">Security</span>
                  <span className={`sc-meta${totp?.enabled ? ' on' : ''}`}>
                    {totp?.enabled ? 'TOTP ON' : totp?.pending ? 'TOTP PENDING' : 'TOTP OFF'}
                  </span>
                </div>
                <div className="sc-desc">
                  Single-operator password plus two-factor codes from your authenticator app.
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className={isMobile ? 'cbtn' : 'btn'} onClick={() => setModal('password')}>
                    Change password
                  </button>
                  <button className={isMobile ? 'cbtn' : 'btn'} onClick={() => setModal('totp')}>
                    {totp?.enabled ? 'Reset TOTP' : 'Set up TOTP'}
                  </button>
                </div>
                <div className="sc-row" style={{ marginTop: 12 }}>
                  <span>Active sessions</span>
                  <span className="meta">
                    {sessions ?? '—'} device{sessions === 1 ? '' : 's'}
                  </span>
                </div>
                <div className="sc-row">
                  <span>Sign out everywhere</span>
                  {isMobile ? <button className="settings-text-button" onClick={() => signOutEverywhere().catch(e => setError(e.message))}>Sign out →</button> : <span className="dl meta" onClick={signOutEverywhere}>
                    Sign out →
                  </span>}
                </div>
              </div>
            </div>
          )}

          {tab === 'data' && (
            <div className="settings-grid">
              {/* Backups */}
              <div className="settings-card">
                <div className="sc-head">
                  <span className="sc-title">Backups</span>
                  <span className="sc-meta">
                    {backups?.length
                      ? `Last backup ${timeLabel(backups[0].uploaded)}`
                      : 'Daily · to R2'}
                  </span>
                </div>
                <div className="sc-desc">
                  Full D1 export, gzipped JSON. Automatic daily run plus manual snapshots; last
                  30 kept. Restore steps: docs/RESTORE-RUNBOOK.md.
                </div>
                <button
                  className={isMobile ? "cbtn pri" : "btn primary"}
                  disabled={busy}
                  onClick={() =>
                    run(async () => {
                      await api('/api/admin/system/backup/run', { method: 'POST' });
                    }, 'Backup written')
                  }
                >
                  Back up now
                </button>
                <div style={{ marginTop: 12 }}>
                  {backups?.map((b) => isMobile ? (
                    <a className="settings-link-row settings-backup-row" key={b.key} href={`/api/admin/system/backups/download?key=${encodeURIComponent(b.key)}`} aria-label={`Download backup ${b.key}`}>
                      <span>{b.key}</span><span className="settings-row-action">{(b.size / 1024).toFixed(1)} KB ↓</span>
                    </a>
                  ) : (
                    <div className="sc-row" key={b.key}>
                      <span>{b.key}</span>
                      <span className="meta">{(b.size / 1024).toFixed(1)} KB</span>
                      <a
                        className="dl"
                        href={`/api/admin/system/backups/download?key=${encodeURIComponent(b.key)}`}
                      >
                        ↓
                      </a>
                    </div>
                  ))}
                  {backups && backups.length === 0 && (
                    <div className="empty-note">No backups yet — the daily cron writes one at 02:00 UTC.</div>
                  )}
                </div>
              </div>

              {/* Trash */}
              <div className="settings-card danger-zone">
                <div className="sc-head">
                  <span className="sc-title">Trash</span>
                  <span className="sc-meta">Purged after 30 days</span>
                </div>
                <div className="sc-desc">
                  Deleted leads, projects, tasks and invoices land here and can be restored.
                  Numbered projects are kept forever so their numbers stay reserved.
                </div>
                {trash === null ? (
                  <SkeletonRows />
                ) : (
                  <>
                    {visibleTrash?.map((t) => isMobile ? (
                      <div className="settings-trash-row" key={`${t.table}:${t.id}`}>
                        <div className="settings-trash-label"><small>{TABLE_LABELS[t.table] ?? t.table}{t.numbered ? ' · NUMBERED' : ''}</small><span>“{t.label}”</span></div>
                        <div className="settings-trash-actions">
                          {t.table === 'projects' && (t.invoice_count ?? 0) > 0 ? <span className="chip trash-number-kept">Number kept · {t.invoice_count} inv.</span> : t.table === 'projects' ? <button className="cbtn dng" disabled={busy} onClick={() => setDeleteProject(t)}>Delete permanently</button> : null}
                          <button className="settings-text-button" disabled={busy} onClick={() => restoreTrashItem(t)}>Restore</button>
                        </div>
                      </div>
                    ) : (
                      <div className="sc-row trash-row" key={`${t.table}:${t.id}`}>
                        <span
                          className="trash-row-label"
                          title={`${TABLE_LABELS[t.table] ?? t.table} — “${t.label}”`}
                          style={{ fontFamily: 'var(--font-body)', fontSize: 12.5 }}
                        >
                          {TABLE_LABELS[t.table] ?? t.table} — “{t.label}”
                        </span>
                        <span className="meta">
                          {t.numbered ? `numbered · ${deletedLabel(t.deleted_at).replace('deleted ', '')}` : deletedLabel(t.deleted_at)}
                        </span>
                        {t.table === 'projects' && (t.invoice_count ?? 0) > 0 ? (
                          <button
                            type="button"
                            className="chip trash-number-kept"
                            disabled
                            title="Projects with invoice history keep their number permanently."
                          >
                            Number kept · {t.invoice_count} inv.
                          </button>
                        ) : t.table === 'projects' ? (
                          <button
                            type="button"
                            className="chip danger"
                            disabled={busy}
                            onClick={() => {
                              const projectNumber = t.project_number ?? null;
                              const numberLabel = projectNumber === null
                                ? 'this unnumbered project'
                                : formatProjectNumber(projectNumber);
                              const consequence = projectNumber === null
                                ? 'This cannot be undone.'
                                : `This frees number ${String(projectNumber).padStart(4, '0')} for reuse and cannot be undone.`;
                              if (!window.confirm(`Permanently delete ${numberLabel} “${t.label}”? ${consequence}`)) return;
                              run(async () => {
                                await api(`/api/admin/system/trash/projects/${t.id}`, { method: 'DELETE' });
                                setMsg('Project permanently deleted');
                              });
                            }}
                          >
                            Delete permanently
                          </button>
                        ) : null}
                        <span className="dl" onClick={() => restoreTrashItem(t)}>
                          Restore
                        </span>
                      </div>
                    ))}
                    {trash.length === 0 && <div className="empty-note">Trash is empty.</div>}
                    {trash.length > TRASH_WINDOW && !showAllTrash && (
                      <button className="show-more" onClick={() => setShowAllTrash(true)}>
                        Show all {trash.length} →
                      </button>
                    )}
                    {trash.length > 0 && (
                      <div style={{ marginTop: 12 }}>
                        <button
                          className={isMobile ? 'cbtn dng' : 'btn danger'}
                          disabled={busy}
                          onClick={() => {
                            if (!emptyArming) {
                              setEmptyArming(true);
                              return;
                            }
                            setEmptyArming(false);
                            run(async () => {
                              const r = await api<{ kept_numbered_projects?: number }>(
                                '/api/admin/system/trash/empty',
                                { method: 'POST' },
                              );
                              setMsg(
                                r.kept_numbered_projects
                                  ? `Trash emptied — ${r.kept_numbered_projects} numbered project${r.kept_numbered_projects === 1 ? '' : 's'} kept`
                                  : 'Trash emptied',
                              );
                            });
                          }}
                        >
                          {emptyArming ? 'Click again to confirm' : 'Empty trash'}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Unlinked artifacts (Wave A3) — proposals self-registered via
                  POST /api/service/artifacts whose client_name didn't
                  confidently match a clients row. Assign moves the entry into
                  project_artifacts on the chosen project. */}
              <div className="settings-card">
                <div className="sc-head">
                  <span className="sc-title">Unlinked artifacts</span>
                  <span className="sc-meta">
                    {unlinkedArtifacts ? `${unlinkedArtifacts.length} awaiting assignment` : '…'}
                  </span>
                </div>
                <div className="sc-desc">
                  A proposal (or other tool) registered itself for a client name that didn’t match
                  anyone here. Pick the project it belongs to.
                </div>
                {(unlinkedArtifacts ?? []).map((u) => isMobile ? (
                  <div className="settings-artifact-row" key={u.id}>
                    <div><span>{ARTIFACT_TOOL_LABELS[u.tool]} · “{u.title ?? u.client_name}”</span><small>{u.title ? `${u.client_name} · ` : ''}received {timeLabel(new Date(u.received_at).toISOString())}</small></div>
                    <button className="settings-text-button" disabled={busy} onClick={() => setAssigning(u.id)}>Assign ›</button>
                  </div>
                ) : (
                  <div className="sc-row" key={u.id}>
                    <span
                      title={`${ARTIFACT_TOOL_LABELS[u.tool]} ${u.title ?? '(untitled)'} — ${u.client_name}`}
                      style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      <span className="meta mono" style={{ marginRight: 8 }}>{ARTIFACT_TOOL_LABELS[u.tool]}</span>
                      {u.title ?? '(untitled)'} — {u.client_name}
                    </span>
                    <span className="meta" style={{ flexShrink: 0 }}>
                      {timeLabel(new Date(u.received_at).toISOString())}
                    </span>
                    <span className="dropdown-anchor">
                      <span className="dl" onClick={() => setAssigning((id) => (id === u.id ? null : u.id))}>
                        Assign →
                      </span>
                      {assigning === u.id && (
                        <Dropdown
                          items={projectOptions.map((p) => ({ value: p.id, label: `${p.name} · ${p.client_name}` }))}
                          onSelect={(projectId) => {
                            setAssigning(null);
                            assignArtifact(u.id, projectId);
                          }}
                          onClose={() => setAssigning(null)}
                          searchable
                        />
                      )}
                    </span>
                  </div>
                ))}
                {unlinkedArtifacts && unlinkedArtifacts.length === 0 && (
                  <div className="empty-note">Queue is empty.</div>
                )}
              </div>

              {/* Integration — Fix 0.2: a live health probe, not secret presence.
                  Green only when the authenticated round-trip actually succeeds;
                  red with the HTTP status when the secret is set but rejected. */}
              <div className="settings-card">
                <div className="sc-head">
                  <span className="sc-title">Dashboards integration</span>
                  <span
                    className={`sc-meta${
                      integration?.healthy ? ' on' : integration?.configured ? ' bad' : ''
                    }`}
                  >
                    {integration === null
                      ? '…'
                      : integration.healthy
                        ? 'Healthy'
                        : integration.configured
                          ? `Error${integration.status ? ` ${integration.status}` : ''}`
                          : 'Not configured'}
                  </span>
                </div>
                <div className="sc-desc">
                  Service binding to the hsp-dashboards worker.{' '}
                  {integration?.healthy
                    ? 'Write secret is set and the authenticated probe succeeded.'
                    : integration?.configured
                      ? `Write secret is set but the dashboard worker rejected it${
                          integration.status ? ` (HTTP ${integration.status})` : ''
                        } — rotate the shared secret so all copies match.`
                      : 'Set the write secret to enable the dashboards module.'}
                </div>
                <div className="sc-row">
                  <span>DASHBOARD_WRITE_SECRET</span>
                  <span className="meta">{integration?.configured ? 'set' : 'not set'}</span>
                </div>
              </div>

              {/* Error log (phase 23) — the Overview "worker errors" attention
                  row lands here. Rows purge after 30 days via the daily cron. */}
              <div className="settings-card">
                <div className="sc-head">
                  <span className="sc-title">Error log</span>
                  <span className="sc-meta">{errors ? `${errors.length} in the last 30 days` : '…'}</span>
                  {errors && errors.length > 0 && (
                    <button
                      className={isMobile ? 'cbtn' : 'btn'}
                      onClick={() => dismissAllErrors().catch((e) => setError(e.message))}
                    >
                      Dismiss all
                    </button>
                  )}
                </div>
                {(errors ?? []).slice(0, 8).map((e) => isMobile ? (
                  <div className="settings-artifact-row" key={e.id}>
                    <div><span>{e.message}</span><small>{e.scope} · {timeLabel(new Date(e.created_at).toISOString())}</small></div>
                    <button className="ib" aria-label={`Dismiss ${e.scope} error`} onClick={() => dismissError(e.id).catch(err => setError(err.message))}>×</button>
                  </div>
                ) : (
                  <div className="sc-row" key={e.id}>
                    <span
                      title={`${e.scope} ${e.message}`}
                      style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      <span className="meta mono" style={{ marginRight: 8 }}>{e.scope}</span>
                      {e.message}
                    </span>
                    <span className="meta" style={{ flexShrink: 0 }}>{timeLabel(new Date(e.created_at).toISOString())}</span>
                    <button
                      className="btn"
                      title={`Dismiss ${e.scope} error`}
                      aria-label={`Dismiss ${e.scope} error`}
                      onClick={() => dismissError(e.id).catch((err) => setError(err.message))}
                    >
                      ×
                    </button>
                  </div>
                ))}
                {errors && errors.length === 0 && (
                  <div className="empty-note">No worker errors — quiet is good.</div>
                )}
              </div>
            </div>
          )}

          {tab === 'automation' && (
            <div className="settings-grid">
              {/* Follow-up tasks (workflow B, 2026-10) — full width, first. */}
              <FollowupTasksCard
                active={tab === 'automation'}
                mobile={isMobile}
                onSaved={flashSaved}
                onError={setFollowupError}
              />
              {/* Email automations (phase 1.8, moved to its own page for
                  Handoff C 2026-07) — the background jobs (reminder ladder,
                  shoot-day and testimonial nudges, waiting-on-client,
                  stale-proposal digest, proposal-signed receipts) have a
                  dedicated hub with toggles, timing knobs, errors, send
                  history and editable templates. This card is just a pointer
                  to it. */}
              <div className="settings-card">
                <div className="sc-head">
                  <span className="sc-title">Email automations</span>
                  <span
                    className={`sc-meta${
                      emailReady?.configured ? (emailReady.paused ? ' warn' : ' on') : ''
                    }`}
                  >
                    {emailReady === null
                      ? '…'
                      : !emailReady.configured
                        ? 'Needs email setup'
                        : emailReady.paused
                          ? 'Email paused'
                          : 'Email live'}
                  </span>
                </div>
                <div className="sc-desc">
                  Every scheduled email and background job — reminder ladder, shoot-day and testimonial
                  nudges, waiting-on-client follow-ups, the stale-proposal digest, proposal-signed
                  receipts — lives on its own page: on/off toggles, timing, errors, send history and
                  editable templates.
                </div>
                <Link className={isMobile ? 'cbtn pri' : 'btn primary'} to="/automations">
                  Open Automations →
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>

      {isMobile && <PickerSheet open={assigning !== null} title="Assign to project" items={projectOptions.map(p => ({ value: p.id, label: p.name, sub: p.client_name }))} onSelect={id => { if (assigning) void assignArtifact(assigning, id); }} onClose={() => setAssigning(null)} searchable />}
      {/* The desktop path confirms with window.confirm(); on mobile that
          native dialog is replaced by a half sheet (rule 3). */}
      {isMobile && deleteProject && (
        <Sheet
          open
          variant="half"
          title="Delete project permanently?"
          context={
            (deleteProject.project_number != null
              ? formatProjectNumber(deleteProject.project_number)
              : null) ?? undefined
          }
          onClose={() => { if (!busy) setDeleteProject(null); }}
          leftAction={<SheetButton disabled={busy} onClick={() => setDeleteProject(null)}>Cancel</SheetButton>}
          rightAction={
            <SheetButton
              tone="danger"
              disabled={busy}
              onClick={() =>
                run(async () => {
                  await api(`/api/admin/system/trash/projects/${deleteProject.id}`, { method: 'DELETE' });
                  setDeleteProject(null);
                }, 'Project permanently deleted')
              }
            >
              Delete
            </SheetButton>
          }
        >
          <div className="settings-confirm">
            “{deleteProject.label}” will be permanently deleted.{' '}
            {deleteProject.project_number != null
              ? `This frees number ${String(deleteProject.project_number).padStart(4, '0')} for reuse and cannot be undone.`
              : 'This cannot be undone.'}
            {error && <p className="error-text" role="alert">{error}</p>}
          </div>
        </Sheet>
      )}
      {modal === 'password' && (
        <ChangePasswordModal
          totpEnabled={!!totp?.enabled}
          onClose={() => setModal(null)}
          onDone={() => setMsg('Password changed')}
        />
      )}
      {modal === 'totp' && (
        <TotpModal
          totp={totp}
          onClose={() => {
            setModal(null);
            load().catch(() => {});
          }}
        />
      )}
      {templateModal && (
        <TemplateModal
          initial={templateModal === 'new' ? null : templateModal}
          onClose={() => setTemplateModal(null)}
          onSaved={() => load().catch(() => {})}
        />
      )}
    </>
  );
}
